import { Property } from '@app/models/Property'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { Button } from 'reactstrap/lib'
import { Channel, User } from 'stream-chat'
import { useChannelMembershipState, useChannelPreviewInfo, useChannelStateContext, useChatContext } from 'stream-chat-react'
import { getPropertyById } from './actions'
import ChannelAvatar from './ChannelAvatar'

import './ChannelHeader.scss'

type IComponentProps = {
  onShowMobileChannelList: () => void
  onShowChannelEditModal: (channel: Channel, action?: 'addMembers') => void
}

const ChannelHeader = (props: IComponentProps) => {
  const { client } = useChatContext()
  const { channel, members, watcherCount } = useChannelStateContext()
  const membership = useChannelMembershipState(channel)
  const { displayTitle } = useChannelPreviewInfo({channel})

  const [idCopied, setIdCopied] = React.useState(false)

  React.useEffect(() => {
    if (idCopied) {
      const timeout = setTimeout(() => setIdCopied(false), 2000)
      return () => clearTimeout(timeout)
    }
  }, [idCopied])

  const copyId = React.useCallback(() => {
    if (idCopied) {
      return
    }
    navigator.clipboard.writeText(channel.id!)
    setIdCopied(true)
  }, [channel.id, idCopied])
  
  const memberCount = React.useMemo(() => members ? Object.keys(members).length : 0, [members])
  const propertyIds = React.useMemo(() => channel.data?.propertyIds as string[] || [], [channel.data])
  const isArchived = React.useMemo(() => !!channel.data?.readOnly, [channel.data?.readOnly])
  const actionsVisible = React.useMemo(() => (
    !isArchived && (channel.data?.created_by as User)?.id === client.userID || membership.role === 'admin'
  ), [isArchived, channel.data?.created_by, client.userID, membership.role])

  return (
    <div className='chat-channel-header'>
      <div className='chat-channel-header-info'>
        <div className='w-100 d-flex align-items-center'>
          <button
            className='chat-channel-header-info-mobile-show-channels-btn'
            onClick={props.onShowMobileChannelList}
          >
            <FontAwesomeIcon
              icon={['far', 'chevron-right']}
              size='lg'
            />
          </button>
          <div className='chat-channel-header-info-avatar'>
            <ChannelAvatar onShowChannelEditModal={props.onShowChannelEditModal} />
          </div>
          <div>
            <button
              className={
                'ml-3 chat-channel-header-info-title ' + (idCopied ? 'chat-channel-header-info-title--copied' : '')
              }
              aria-label='Copy Channel ID'
              onClick={copyId}
            >
              {displayTitle}
              <div className='chat-channel-header-info-title-label'>
                {idCopied ? 'Copied!' : 'Copy ID'}
              </div>
            </button>
          </div>
        </div>
        <div className='chat-channel-header-info-sub'>
          <div className='chat-channel-header-info-sub-members' data-archived={isArchived}>
            {memberCount} {memberCount === 1 ? 'member' : 'members'}, {watcherCount} online now
          </div>
          {isArchived && <div>Archived</div>}

          {actionsVisible && (
            <div className='chat-channel-header-info-sub-actions'>
              <Button
                color='primary'
                size='sm'
                aria-label='Add Members'
                onClick={() => props.onShowChannelEditModal(channel, 'addMembers')}
              >
                <FontAwesomeIcon icon={['fas', 'user-plus']} />
              </Button>
            </div>
          )}
        </div>
      </div>
      {propertyIds.length !== 0 && <PropertiesList propertyIds={propertyIds} />}
    </div>
  )
}
export default ChannelHeader

const PropertiesList = (props: { propertyIds: string[] }) => {
  const [properties, setProperties] = React.useState<(Property | Error)[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let active = true
    load()
    return () => { active = false }

    async function load() {
      const p = await Promise.all(props.propertyIds.map(async (id) => {
        try {
          return await getPropertyById(id)
        } catch (error) {
          return error as Error
        }
      }))
      if (!active) { // Data race guard
        return
      }
      setProperties(p)
      setLoading(false)
    }
  }, [props.propertyIds])

  return (
    <div className='chat-channel-header-properties'>
      <div className='text-small'>
        Associated Properties
      </div>
      {loading ? (
        <div>Loading...</div>
      ) : (
        properties.map((p, index) => (
          <div key={index}>
            {p instanceof Error ? (
              <div className='text-danger'>{p.message}</div>
            ) : (
              <a className='chat-channel-header-properties-item' href={`/properties/property/${p.id}`}>
                {p.name}
              </a>
            )}
          </div>
        ))
      )}
    </div>
  )
}
